import React, { useEffect, useRef, useState } from 'react';
import { EntityFilterDropDownMenu } from '~/components/entityViewer/entitiesDropDownFilter/entityFilterControls/EntityFilterDropDownMenu';
import { FilterChipContainer } from '~/components/entityViewer/entitiesDropDownFilter/entityFilterControls';
import { isEqual } from 'lodash';

export function EntityDropDownFilterContainer({
    filterConfigs = [],
    title = 'Filters',
    initialValues = {},
    onChange,
    buttonLabel = 'Filters',
    className = '',
    autoApply = false,
}) {
    // Draft values are edited inside the dropdown; applied values drive chips and external consumers
    const [draftValues, setDraftValues] = useState(() => ({ ...initialValues }));
    const [appliedValues, setAppliedValues] = useState(() => ({ ...initialValues }));
    const [menuOpen, setMenuOpen] = useState(false);
    const previousInitialValuesRef = useRef(initialValues);

    useEffect(() => {
        if (isEqual(previousInitialValuesRef.current, initialValues)) {
            return;
        }
        previousInitialValuesRef.current = initialValues;
        setDraftValues({ ...initialValues });
        setAppliedValues({ ...initialValues });
    }, [initialValues]);

    // Chip container will derive chips from values; no chip building logic here

    const handleMenuChange = (next) => {
        if (autoApply) {
            setDraftValues(next);
            setAppliedValues(next);
            if (onChange) onChange(next);
        } else {
            setDraftValues(next);
        }
    };

    const handleApply = (next) => {
        const finalValues = next ?? draftValues;
        setAppliedValues(finalValues);
        setDraftValues(finalValues);
        if (onChange) onChange(finalValues);
    };

    const handleDiscard = () => {
        setDraftValues(appliedValues);
    };

    const hasPendingChanges = !autoApply && !isEqual(appliedValues, draftValues);

    const handleDelete = (toDelete) => {
        const deletions = Array.isArray(toDelete) ? toDelete : [toDelete];
        const nextDraft = { ...draftValues };
        const nextApplied = { ...appliedValues };
        for (const item of deletions) {
            if (!item?.filterKey) continue;
            const key = item.filterKey;
            // For multi-value selects (values stored as arrays), remove a single value; otherwise remove the key
            if (Array.isArray(nextApplied[key])) {
                const removeVal = String(item.value);
                const strip = (arr) => Array.from((arr || []).map(String).filter((v) => v !== removeVal));
                const d = strip(nextDraft[key]);
                const a = strip(nextApplied[key]);
                if (!d || d.length === 0) delete nextDraft[key];
                else nextDraft[key] = d;
                if (!a || a.length === 0) delete nextApplied[key];
                else nextApplied[key] = a;
            } else {
                delete nextDraft[key];
                delete nextApplied[key];
            }
        }
        setDraftValues(nextDraft);
        setAppliedValues(nextApplied);
        if (onChange) onChange(nextApplied);
    };

    const handleChipClick = () => {
        setMenuOpen(true);
    };

    return (
        <div className={`flex items-center gap-2 ${className}`}>
            <EntityFilterDropDownMenu
                title={title}
                autoApply={autoApply}
                filterConfigs={filterConfigs}
                initialValues={draftValues}
                onChange={handleMenuChange}
                onApply={handleApply}
                onDiscard={handleDiscard}
                hasPendingChanges={hasPendingChanges}
                open={menuOpen}
                onOpenChange={setMenuOpen}
                buttonLabel={buttonLabel}
            />
            <div className="flex-1 min-w-0">
                <FilterChipContainer
                    className="mt-0"
                    appliedValues={appliedValues}
                    draftValues={draftValues}
                    onApply={() => handleApply()}
                    filterConfigs={filterConfigs}
                    onDelete={handleDelete}
                    onChipClick={handleChipClick}
                />
            </div>
        </div>
    );
}

export default EntityDropDownFilterContainer;
